import { useEffect, useRef, useState } from "react";

import { AmplitudeFlagKey, AmplitudeVariantValue } from "@simplyk/common";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useAmplitudeFeatureFlag } from "@/common/hooks/amplitude/useAmplitudeFeatureFlag";
import { trpc } from "@/common/helpers/trpc";

export type AiDescriptionWizardVariant = "a" | "b" | null;

export interface AiDescriptionWizardExperiment {
  variant: AiDescriptionWizardVariant;
  isWizardEnabled: boolean;
  hasNpoContext: boolean;
  isResolved: boolean;
  loading: boolean;
}

interface DecidedExperiment {
  variant: AiDescriptionWizardVariant;
  hasNpoContext: boolean;
  isResolved: boolean;
}

const EXPERIMENT_RESOLUTION_TIMEOUT_MS = 2000;

export const useAiDescriptionWizardExperiment = (): AiDescriptionWizardExperiment => {
  const { organization } = useCurrentUserContext();

  const { value, loading } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.AiDescriptionWizard,
    organizationId: organization?.id,
    organizationProperties: { organization_zeffy_id: organization?.id || "" },
    skip: !organization?.id,
  });

  const { data: npoContextData, isLoading: isNpoContextLoading } = trpc.hasDescriptionWizardNpoContext.useQuery(
    undefined,
    { enabled: Boolean(organization?.id) }
  );

  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), EXPERIMENT_RESOLUTION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  const decidedRef = useRef<DecidedExperiment | undefined>(undefined);
  if (decidedRef.current === undefined && ((!loading && !isNpoContextLoading) || timedOut)) {
    decidedRef.current = {
      variant: value === AmplitudeVariantValue.B ? "b" : value === AmplitudeVariantValue.A ? "a" : null,
      hasNpoContext: npoContextData?.hasNpoContext === true,
      isResolved: !loading && !isNpoContextLoading,
    };
  }

  const decided = decidedRef.current;

  return {
    variant: decided?.variant ?? null,
    isWizardEnabled: decided?.variant === "b" && decided.hasNpoContext,
    hasNpoContext: decided?.hasNpoContext ?? false,
    isResolved: decided?.isResolved ?? false,
    loading: decided === undefined,
  };
};
